import React, { useState } from 'react';
import { Check, LocateFixed, Minus, Moon, Plus, Sun, SunMoon } from 'lucide-react';
import { cn } from '../utils/cn';
import { rupees } from './shopApi';
import type { BillingMode, PlanOption, PlanShift, ProfileForm, SignupInfo, SubscriptionChoice } from './customerApi';

// Form pieces shared by Sign up (new customers) and the signed-in Account
// screen: the address (same fields as the staff Customers form) and the daily
// milk plan picker.

export const inputCls = 'w-full rounded-lg border border-ink-300/50 bg-white px-3 py-2.5 text-base focus:border-brand-green-600 focus:outline-none focus:ring-2 focus:ring-brand-green-100';
const isoDay = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const dayLabel = (iso: string) => new Date(`${iso}T00:00:00`).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
const L = (n: number) => `${Number(n.toFixed(2))} L`;

export function Field({ label, hint, error, children }: { label: string; hint?: string; error?: string | null; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1 text-sm font-medium">
      <span>{label}{hint && <span className="ml-1 font-normal text-ink-400">{hint}</span>}</span>
      {children}
      {error && <span className="text-xs font-medium text-critical-600">{error}</span>}
    </label>
  );
}

// ---------- address ----------
export const EMPTY_ADDRESS: ProfileForm = { name: '', email: '', apartment_name: '', house_number: '', address: '', city: '', state: '', pincode: '' };

export function addressErrors(a: ProfileForm, pincodes: string[]): Partial<Record<keyof ProfileForm, string>> {
  const e: Partial<Record<keyof ProfileForm, string>> = {};
  if (!a.house_number.trim()) e.house_number = 'Your flat / house number';
  if (a.address.trim().length < 3) e.address = 'Street and area, so the agent can find you';
  if (!/^\d{6}$/.test(a.pincode.trim())) e.pincode = 'The 6-digit PIN code';
  else if (pincodes.length && !pincodes.includes(a.pincode.trim())) e.pincode = `Sorry, we don't deliver to ${a.pincode.trim()} yet`;
  return e;
}

export function AddressFields({ value, onChange, errors = {}, hasLocation }: {
  value: ProfileForm; onChange: (v: ProfileForm) => void; errors?: Partial<Record<keyof ProfileForm, string>>; hasLocation?: boolean;
}) {
  const [loc, setLoc] = useState<'idle' | 'busy' | 'done' | 'failed'>(value.latitude != null || hasLocation ? 'done' : 'idle');
  const set = (k: keyof ProfileForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => onChange({ ...value, [k]: e.target.value });
  const locate = () => {
    if (!navigator.geolocation) { setLoc('failed'); return; }
    setLoc('busy');
    navigator.geolocation.getCurrentPosition(
      (p) => { onChange({ ...value, latitude: Number(p.coords.latitude.toFixed(6)), longitude: Number(p.coords.longitude.toFixed(6)) }); setLoc('done'); },
      () => setLoc('failed'),
      { enableHighAccuracy: true, timeout: 15000 },
    );
  };
  return (
    <div className="grid grid-cols-2 gap-3">
      <div className="col-span-2"><Field label="Apartment / building name" hint="(if any)"><input className={inputCls} autoComplete="address-line2" value={value.apartment_name} onChange={set('apartment_name')} placeholder="e.g. Prestige Lakeside" /></Field></div>
      <Field label="Flat / house no." error={errors.house_number}><input className={inputCls} value={value.house_number} onChange={set('house_number')} placeholder="e.g. B-204" /></Field>
      <Field label="PIN code" error={errors.pincode}><input className={inputCls} inputMode="numeric" autoComplete="postal-code" maxLength={6} value={value.pincode} onChange={set('pincode')} /></Field>
      <div className="col-span-2"><Field label="Street, area, landmark" error={errors.address}><textarea className={inputCls} rows={2} autoComplete="street-address" value={value.address} onChange={set('address')} placeholder="e.g. Varthur Main Road, near the temple" /></Field></div>
      <Field label="City"><input className={inputCls} autoComplete="address-level2" value={value.city} onChange={set('city')} /></Field>
      <Field label="State"><input className={inputCls} autoComplete="address-level1" value={value.state} onChange={set('state')} /></Field>
      <button type="button" onClick={locate} disabled={loc === 'busy'}
        className={cn('col-span-2 inline-flex items-center justify-center gap-2 rounded-lg border py-2.5 text-sm font-semibold',
          loc === 'done' ? 'border-success-600 bg-success-50 text-success-700' : 'border-brand-green-600 text-brand-green-800')}>
        {loc === 'done' ? <Check className="h-4 w-4" /> : <LocateFixed className="h-4 w-4" />}
        {loc === 'busy' ? 'Finding you…' : loc === 'done' ? 'Home location saved — tap to update' : 'Use my current location (when at home)'}
      </button>
      {loc === 'failed' && <p className="col-span-2 text-xs text-ink-500">Couldn't get your location — that's fine, the address is enough. You can allow location in the browser and try again.</p>}
    </div>
  );
}

// ---------- daily milk ----------
export interface MilkDraft { planId: string; shift: PlanShift; litres: number; am: number; pm: number; start: string; billing: BillingMode | '' }

export function draftFor(plan: PlanOption, info: SignupInfo, prev?: MilkDraft): MilkDraft {
  const litres = Number(plan.litres_per_day);
  const am = plan.morning_litres != null ? Number(plan.morning_litres) : Math.max(0.5, Math.round(litres) / 2 || 0.5);
  const pm = plan.evening_litres != null ? Number(plan.evening_litres) : am;
  return { planId: plan.id, shift: plan.shift, litres, am, pm, start: prev?.start || info.first_start, billing: prev?.billing ?? (info.billing === 'choose' ? '' : info.billing) };
}

export function toChoice(d: MilkDraft): SubscriptionChoice {
  const both = d.shift === 'Both';
  return { plan_id: d.planId, shift: d.shift, litres_per_day: both ? d.am + d.pm : d.litres, ...(both ? { morning_litres: d.am, evening_litres: d.pm } : {}), start_date: d.start };
}

export const milkReady = (d: MilkDraft | null, info: SignupInfo) => !!d && !!d.planId && !!d.start && (info.billing !== 'choose' || !!d.billing);

export function milkSummary(d: MilkDraft, info: SignupInfo): string {
  const plan = info.plans.find((p) => p.id === d.planId);
  const when = d.shift === 'Both' ? `${L(d.am)} morning + ${L(d.pm)} evening` : `${L(d.litres)} every ${d.shift === 'Morning' ? 'morning' : 'evening'}`;
  return `${when}${plan ? ` (${plan.name})` : ''}, from ${dayLabel(d.start)}`;
}

function Stepper({ value, onChange, label }: { value: number; onChange: (n: number) => void; label: string }) {
  const btn = 'flex h-10 w-10 items-center justify-center rounded-full border border-brand-green-600 text-brand-green-800 disabled:border-ink-300 disabled:text-ink-300';
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg bg-white px-3 py-2">
      <span className="text-sm font-medium">{label}</span>
      <span className="flex items-center gap-3">
        <button type="button" aria-label={`Less ${label}`} className={btn} disabled={value <= 0.5} onClick={() => onChange(Math.max(0.5, value - 0.5))}><Minus className="h-4 w-4" /></button>
        <span className="w-16 text-center text-lg font-bold tabular-nums">{L(value)}</span>
        <button type="button" aria-label={`More ${label}`} className={btn} disabled={value >= 10} onClick={() => onChange(Math.min(10, value + 0.5))}><Plus className="h-4 w-4" /></button>
      </span>
    </div>
  );
}

export function MilkPlanPicker({ info, value, onChange }: { info: SignupInfo; value: MilkDraft | null; onChange: (d: MilkDraft) => void }) {
  const plan = info.plans.find((p) => p.id === value?.planId);
  const days = (() => {
    const out: string[] = [];
    const d = new Date(`${info.first_start}T00:00:00`);
    for (let i = 0; i < 14; i++) { out.push(isoDay(d)); d.setDate(d.getDate() + 1); }
    return out;
  })();
  const perDay = value ? (value.shift === 'Both' ? value.am + value.pm : value.litres) : 0;
  const rate = Number(plan?.rate_per_litre ?? 0);
  const set = (patch: Partial<MilkDraft>) => value && onChange({ ...value, ...patch });
  const choice = (active: boolean) => cn('rounded-xl border p-3 text-left', active ? 'border-brand-green-700 bg-brand-green-50 ring-1 ring-brand-green-700' : 'border-ink-300/40 bg-white');

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <p className="text-sm font-semibold">Choose a plan</p>
        {info.plans.map((p) => (
          <button key={p.id} type="button" onClick={() => onChange(draftFor(p, info, value ?? undefined))} className={choice(value?.planId === p.id)}>
            <span className="flex items-center justify-between gap-2">
              <span className="font-semibold text-brand-green-900">{p.name}</span>
              <span className="text-sm font-bold">{rupees(p.rate_per_litre)}/L</span>
            </span>
            <span className="block text-xs text-ink-500">
              {L(Number(p.litres_per_day))} a day · {p.shift === 'Both' ? 'morning + evening' : p.shift === 'Morning' ? 'every morning' : 'every evening'}
            </span>
          </button>
        ))}
      </div>

      {value && plan && (
        <>
          <div className="flex flex-col gap-2">
            <p className="text-sm font-semibold">When should we deliver?</p>
            <div className="grid grid-cols-3 gap-2">
              {([['Morning', Sun, 'Morning'], ['Evening', Moon, 'Evening'], ['Both', SunMoon, 'Both']] as const).map(([s, Icon, label]) => (
                <button key={s} type="button" onClick={() => set(s === 'Both' && value.shift !== 'Both' ? { shift: s, am: Math.max(0.5, value.litres / 2), pm: Math.max(0.5, value.litres / 2) } : { shift: s, litres: s !== 'Both' && value.shift === 'Both' ? value.am + value.pm : value.litres })}
                  className={cn('flex flex-col items-center gap-1 rounded-lg border py-2 text-xs font-semibold', value.shift === s ? 'border-brand-green-700 bg-brand-green-50 text-brand-green-800' : 'border-ink-300/40 bg-white text-ink-600')}>
                  <Icon className="h-5 w-5" />{label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <p className="text-sm font-semibold">How much milk?</p>
            {value.shift === 'Both' ? (
              <>
                <Stepper label="Morning" value={value.am} onChange={(am) => set({ am })} />
                <Stepper label="Evening" value={value.pm} onChange={(pm) => set({ pm })} />
              </>
            ) : <Stepper label="Per day" value={value.litres} onChange={(litres) => set({ litres })} />}
            <p className="text-xs text-ink-500">{L(perDay)} a day × {rupees(rate)}/L = <b>{rupees(perDay * rate)} a day</b> (about {rupees(perDay * rate * 30)} a month)</p>
          </div>
          <div className="flex flex-col gap-2">
            <p className="text-sm font-semibold">Start from</p>
            <div className="no-scrollbar -mx-1 flex gap-1.5 overflow-x-auto px-1 pb-1">
              {days.map((d, i) => (
                <button key={d} type="button" onClick={() => set({ start: d })} className={cn('shrink-0 rounded-lg border px-2.5 py-1.5 text-xs', value.start === d ? 'border-brand-green-700 bg-brand-green-50 font-bold text-brand-green-800' : 'border-ink-300/40 bg-white')}>
                  {i === 0 && info.first_start === isoDay(new Date(Date.now() + 86_400_000)) ? 'Tomorrow' : dayLabel(d)}
                </button>
              ))}
            </div>
          </div>
          {info.billing === 'choose' && (
            <div className="flex flex-col gap-2">
              <p className="text-sm font-semibold">How would you like to pay?</p>
              <button type="button" onClick={() => set({ billing: 'Prepaid' })} className={choice(value.billing === 'Prepaid')}>
                <span className="block font-semibold">Prepaid wallet</span>
                <span className="block text-xs text-ink-500">Recharge by UPI first; each delivery is taken from the wallet. We remind you when it runs low.</span>
              </button>
              <button type="button" onClick={() => set({ billing: 'Postpaid' })} className={choice(value.billing === 'Postpaid')}>
                <span className="block font-semibold">Pay later — monthly bill</span>
                <span className="block text-xs text-ink-500">Get milk first, pay the month's bill by UPI within 7 days after the month ends.</span>
              </button>
            </div>
          )}
          {info.billing === 'Prepaid' && <p className="rounded-lg bg-warning-50 p-3 text-xs text-warning-700">Daily milk is prepaid: recharge your wallet by UPI from your account before the first delivery.</p>}
        </>
      )}
    </div>
  );
}
